import en from "../i18n/en.json";
import fr from "../i18n/fr.json";

/**
 * Key-based i18n. Every user-visible string lives in src/i18n/{en,fr}.json and
 * never inline in code (Claude.md conventions, ARCHITECTURE.md §13).
 *
 * Both dictionaries are bundled — together they are a few KB, which is cheaper
 * than a fetch and avoids a flash of untranslated text on first paint.
 */

export type TranslationKey = keyof typeof en;
export type Lang = "en" | "fr";

/**
 * Typing fr as the exact key set of en makes a missing or stray French key a
 * compile error rather than a runtime fallback nobody notices.
 */
const DICTIONARIES: Record<Lang, Record<TranslationKey, string>> = {
    en,
    fr
};

const STORAGE_KEY = "o365x.lang";

function detectLang(): Lang {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "fr") {
        return stored;
    }
    return navigator.language.toLowerCase().startsWith("fr") ? "fr" : "en";
}

let current: Lang = detectLang();

export function getLang(): Lang {
    return current;
}

/**
 * Notified after every language switch — for text `applyTranslations()`
 * cannot reach on its own. That sweep only rewrites elements still carrying
 * a `data-i18n` key; anything composed from more than one key (module
 * descriptions merging description + permission text) or set imperatively
 * from application state (a status line, a panel's header summary) needs to
 * be recomputed by whoever owns it. Panels with either of those register
 * here instead of re-deriving a separate mechanism each time.
 */
const langListeners = new Set<() => void>();

export function onLangChange(listener: () => void): () => void {
    langListeners.add(listener);
    return () => langListeners.delete(listener);
}

export function setLang(lang: Lang): void {
    current = lang;
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
    applyTranslations();
    for (const listener of langListeners) {
        listener();
    }
}

/** Look up `key`, substituting `{placeholder}` tokens from `params`. */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
    const template = DICTIONARIES[current][key];
    if (!params) {
        return template;
    }
    return template.replace(/\{(\w+)\}/g, (match, name: string) => {
        const value = params[name];
        return value === undefined ? match : String(value);
    });
}

/**
 * Applies translations to every element carrying `data-i18n`. Re-run on every
 * language change; elements rendered later must call this again or use t()
 * directly.
 */
export function applyTranslations(root: ParentNode = document): void {
    for (const el of root.querySelectorAll<HTMLElement>("[data-i18n]")) {
        const key = el.dataset["i18n"] as TranslationKey | undefined;
        if (key && key in DICTIONARIES[current]) {
            el.textContent = t(key);
        }
    }
}

export function initI18n(): void {
    document.documentElement.lang = current;
    applyTranslations();
}
